import { Link } from "@tanstack/react-router";
import { GraduationCap, Plus, Search } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";

export interface DealDashboardHeaderProps {
  /**
   * The workspace search input. "Search deals" scrolls it into
   * view and focuses it. Nullable because the input mounts in the
   * workspace (wired in Slice D4); the handler is a safe no-op until then.
   */
  searchRef: React.RefObject<HTMLInputElement | null>;
  /**
   * RACCPE-22: when true (a BDM with outstanding compulsory training), the
   * register CTA is replaced with a link to the outstanding training. Defaults
   * to false so non-lockout callers are unaffected.
   */
  trainingBlocked?: boolean;
}

/**
 * Primary-tasks header for the partner deals dashboard: register a deal,
 * jump to search, and (PO only) the deferred company-Deals surface.
 */
export function DealDashboardHeader({
  searchRef,
  trainingBlocked = false,
}: DealDashboardHeaderProps) {
  function focusSearch() {
    const input = searchRef.current;
    if (!input) return;
    input.scrollIntoView({ behavior: "smooth", block: "center" });
    // Focus after the smooth-scroll starts so the caret lands reliably
    // once the input is in view.
    window.setTimeout(() => input.focus(), 150);
  }

  return (
    // CHP-871: the header (title + primary actions) scrolls away with the page
    // rather than staying pinned — the old `sticky top-14` framing kept the
    // "Your deals, up close." title fixed, which read as a layout bug.
    <div className="mb-6">
      <header className="mb-4">
        {/* Review-r2 (91d1f452, supersedes the PO-specific 3456ad42): both BDMs
            and POs get the personal-pipeline framing. */}
        <h1 className="text-2xl font-semibold tracking-tight">Your deals, up close.</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Register deals, track their progress, and act the moment something needs attention.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        {trainingBlocked ? (
          // CHP-889/890 (+876): primary (pink) CTA, "Complete training to
          // register deals".
          <Button asChild data-testid="register-blocked-training">
            <Link to="/dashboard/partner/training">
              <GraduationCap className="h-4 w-4" aria-hidden /> Complete training to register deals
            </Link>
          </Button>
        ) : (
          <Button asChild>
            <Link to="/dashboard/partner/deals/new">
              <Plus className="h-4 w-4" aria-hidden /> Register a deal
            </Link>
          </Button>
        )}

        <Button variant="outline" onClick={focusSearch}>
          <Search className="h-4 w-4" aria-hidden /> Search deals
        </Button>
      </div>
    </div>
  );
}
